import { expandProfileUrl } from '@/lib/profile-url';
import { MENTION_REGEX } from '@/lib/utils/regex';

/**
 * Client-safe URL utility functions (no database imports)
 * These functions can be safely used in client components
 */

/**
 * Twitter URL entity structure from API responses
 */
interface TwitterUrlEntity {
  display_url?: string;
  displayUrl?: string;
  expanded_url?: string;
  expandedUrl?: string;
  url: string;
}

/**
 * Twitter entities structure
 */
interface TwitterEntities {
  description?: {
    urls?: TwitterUrlEntity[];
  };
  url?: {
    urls?: TwitterUrlEntity[];
  };
}

/**
 * Extract expanded URLs from Twitter entities
 * Handles both bio/description URLs and profile URLs
 */
export function getExpandedUrls(_text: string, entities?: TwitterEntities): Map<string, string> {
  const urlMap = new Map<string, string>();

  if (!entities) {
    return urlMap;
  }

  // Handle description entities (URLs in bio)
  if (entities.description?.urls) {
    for (const urlEntity of entities.description.urls) {
      // Use camelCase - entities from database via Kysely
      if (urlEntity.url && urlEntity.expandedUrl) {
        urlMap.set(urlEntity.url, urlEntity.expandedUrl);
      }
    }
  }

  // Handle profile URL entity
  if (entities.url?.urls) {
    for (const urlEntity of entities.url.urls) {
      // Use camelCase - entities from database via Kysely
      if (urlEntity.url && urlEntity.expandedUrl) {
        urlMap.set(urlEntity.url, urlEntity.expandedUrl);
      }
    }
  }

  return urlMap;
}

/**
 * Replace t.co URLs in text with their expanded versions
 */
export function expandUrlsInText(text: string, entities?: TwitterEntities): string {
  if (!(entities && text)) {
    return text;
  }

  const urlMap = getExpandedUrls(text, entities);

  let expandedText = text;
  for (const [shortUrl, expandedUrl] of urlMap) {
    expandedText = expandedText.replaceAll(shortUrl, expandedUrl);
  }

  return expandedText;
}

/**
 * Get the display URL (without protocol) from an expanded URL
 */
export function getDisplayUrl(url: string): string {
  try {
    const urlObject = new URL(url);
    let display = urlObject.hostname + urlObject.pathname;
    // Remove trailing slash if it's just the domain
    if (display.endsWith('/') && urlObject.pathname === '/') {
      display = display.slice(0, -1);
    }
    // Truncate long URLs
    if (display.length > 30) {
      display = `${display.slice(0, 27)}...`;
    }
    return display;
  } catch {
    // If URL parsing fails, return the original
    return url;
  }
}

/**
 * Extract the expanded profile URL from entities
 */
export function getExpandedProfileUrl(url: string | undefined, entities?: TwitterEntities): string | undefined {
  if (!url) {
    return;
  }

  const expanded = expandProfileUrl(url, entities);
  return typeof expanded === 'string' && expanded.length > 0 ? expanded : undefined;
}

/**
 * Get display and expanded URLs from Twitter entities
 * Returns both the display-friendly URL and the actual expanded URL
 */
export function getProfileUrls(
  url: string | undefined,
  entities?: TwitterEntities
): { display: string; expanded: string } | undefined {
  if (!url) {
    return;
  }

  const expandedUrl = getExpandedProfileUrl(url, entities);
  if (!expandedUrl) {
    return;
  }

  const displayUrl = getDisplayUrl(expandedUrl);

  return {
    display: displayUrl,
    expanded: expandedUrl,
  };
}

/**
 * Common regex patterns. Keep minimal and focused to avoid overuse.
 */
export const mentionRegex = MENTION_REGEX;

export const URL_PATTERNS = {
  /** Matches @mentions (@username) */
  MENTION: mentionRegex,
} as const;
